const colors = {
    resource: {background: '#dbeafe', border: '#3b82f6'},
    chunk: {background: '#f1f5f9', border: '#94a3b8'},
    concept: {background: '#d1fae5', border: '#10b981'},
    assertion: {background: '#ffedd5', border: '#f97316'},
};
const relationLabels = {
    contains: 'شامل', mentions: 'اشاره به', supports: 'پشتیبانی می‌کند',
    subject: 'فاعل', object: 'مفعول', source: 'منبع', target: 'مقصد',
    uses: 'استفاده می‌کند از', requires: 'نیاز دارد به', part_of: 'بخشی از',
    prerequisite_of: 'پیش‌نیازِ', causes: 'باعث می‌شود',
    contrasts_with: 'در تقابل با', extends: 'گسترش می‌دهد',
    optimizes: 'بهینه می‌کند', is_a: 'نوعی از', related_to: 'مرتبط با',
};

export const getNodeLabel = (node) => String(node.label || relationLabels[node.predicate] || node.predicate || node.id);
export const getRelationLabel = (edge) => {
    const raw = edge.label || edge.predicate || edge.relation || edge.type || 'مرتبط با';
    return relationLabels[raw] || String(raw).replace(/_/g, ' ');
};

// Keep the original records for the inspector; normalize IDs only for vis-network.
export function buildGraphData(graph) {
    const records = [...new Map((graph?.nodes || [])
        .filter((node) => node.id != null)
        .map((node) => [String(node.id), node])).values()];
    const ids = new Set(records.map((node) => String(node.id)));
    return {
        nodes: records.map((record) => ({
            id: String(record.id),
            label: getNodeLabel(record).length > 25 ? `${getNodeLabel(record).slice(0, 24)}…` : getNodeLabel(record),
            shape: 'circle', size: record.type === 'resource' ? 42 : record.type === 'chunk' ? 31 : 37,
            color: colors[record.type] || {background: '#ede9fe', border: '#8b5cf6'},
            record,
        })),
        edges: (graph?.edges || [])
            .filter((edge) => ids.has(String(edge.source)) && ids.has(String(edge.target)))
            .map((record, index) => ({
                id: `edge-${index}`, from: String(record.source), to: String(record.target),
                label: getRelationLabel(record), record,
            })),
    };
}

export const graphNeighbors = (data, id) => {
    const key = String(id);
    return [...new Set(data.edges.flatMap((edge) => edge.from === key ? [edge.to] : edge.to === key ? [edge.from] : []))];
};

export function initialVisibleIds(data, limit = 45) {
    if (!data.nodes.length) return new Set();
    const degree = new Map(data.nodes.map((node) => [node.id, graphNeighbors(data, node.id).length]));
    const rank = (ids) => ids.sort((a, b) => (degree.get(b) || 0) - (degree.get(a) || 0));
    const root = data.nodes.find((node) => node.record.type === 'resource')?.id
        || rank(data.nodes.map((node) => node.id))[0];
    const visible = new Set([root]);
    const direct = rank(graphNeighbors(data, root)).slice(0, Math.min(12, limit - 1));
    direct.forEach((id) => visible.add(id));
    const second = rank([...new Set(direct.flatMap((id) => graphNeighbors(data, id)))]
        .filter((id) => !visible.has(id)));
    for (const id of second) {
        if (visible.size >= limit) break;
        visible.add(id);
    }
    if (visible.size < limit) {
        for (const id of rank(data.nodes.map((node) => node.id).filter((id) => !visible.has(id)))) {
            if (visible.size >= limit) break;
            visible.add(id);
        }
    }
    return visible;
}

export function expandVisibleIds(data, visible, id, batchSize = 30) {
    const next = new Set(visible);
    next.add(String(id));
    const candidates = graphNeighbors(data, id).filter((neighbor) => !next.has(neighbor));
    const degree = (key) => graphNeighbors(data, key).length;
    candidates.sort((a, b) => degree(b) - degree(a));
    candidates.slice(0, batchSize).forEach((neighbor) => next.add(neighbor));
    return next;
}

export function visibleGraph(data, ids, nodeType = '', edgeType = '') {
    const shownNodes = data.nodes.filter((node) => ids.has(node.id) && (!nodeType || node.record.type === nodeType));
    const shownIds = new Set(shownNodes.map((node) => node.id));
    const shownEdges = data.edges.filter((edge) => shownIds.has(edge.from) && shownIds.has(edge.to)
        && (!edgeType || String(edge.record.type || edge.record.predicate || edge.record.relation || '') === edgeType));
    return {nodes: shownNodes, edges: shownEdges};
}
